import React, { useEffect, useState } from 'react';
import GatewayV2Services from '../../services/externalServices/TicketServices/gatewayV2Services';
import TheatreServices from '../../services/externalServices/TicketServices/theatreServices';

export default function RefundsV2() {
  // Search state
  const [phone, setPhone] = useState('');
  const [serviceType, setServiceType] = useState('Tickets');
  const [serviceId, setServiceId] = useState('');
  const [theatres, setTheatres] = useState([]);
  const [items, setItems] = useState([]);
  const [searching, setSearching] = useState(false);

  // Action state
  const [merchantOrderId, setMerchantOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [otp, setOtp] = useState('');
  const [refundId, setRefundId] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showManual, setShowManual] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await TheatreServices.listTheatres();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setTheatres(list);
      } catch { setTheatres([]); }
    })();
  }, []);

  const call = async (fn) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const resp = await fn();
      setResult(resp?.data || resp);
    } catch (e) {
      setError(e?.message || 'Operation failed');
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async () => {
    setSearching(true); setError(''); setItems([]);
    try {
      const payload = { phone, serviceType };
      if (serviceType === 'Tickets' && serviceId) payload.serviceId = serviceId;
      const res = await GatewayV2Services.searchPayments(payload);
      const list = res?.data?.items || res?.data || res?.items || [];
      setItems(Array.isArray(list) ? list : []);
    } catch { setError('Search failed'); }
    finally { setSearching(false); }
  };

  const submitRefund = () => {
    if (!merchantOrderId) { setError('merchantOrderId required'); return; }
    (async()=>{
      setLoading(true); setError(''); setResult(null);
      try {
        const payload = { merchantOrderId };
        if (amount) payload.amount = Number(amount);
        if (reason) payload.reason = reason;
        if (otp) payload.otp = otp;
        const resp = await GatewayV2Services.refund(payload);
        setResult(resp?.data || resp);
        // After refund, prefill lastRefundId from session
        await prefillFromSession(merchantOrderId);
      } catch (e) { setError(e?.message || 'Operation failed'); }
      finally { setLoading(false); }
    })();
  };

  const submitVoid = () => {
    if (!merchantOrderId) { setError('merchantOrderId required'); return; }
    const payload = { merchantOrderId };
    if (reason) payload.reason = reason;
    if (otp) payload.otp = otp;
    call(() => GatewayV2Services.voidPayment(payload));
  };

  const checkStatus = () => {
    if (!merchantOrderId) { setError('merchantOrderId required'); return; }
    call(() => GatewayV2Services.refundStatus(merchantOrderId));
  };

  const prefillFromSession = async (id) => {
    try {
      const s = await GatewayV2Services.getSession(id);
      const data = s?.data?.data || s?.data || s;
      if (data?.lastRefundId) setRefundId(data.lastRefundId);
    } catch (_) { /* ignore */ }
  };

  const reverseRefund = () => {
    if (!merchantOrderId || !refundId) { setError('merchantOrderId and refundId required'); return; }
    const payload = { merchantOrderId, refundId };
    if (reason) payload.reason = reason;
    call(() => GatewayV2Services.refundUpdate(payload));
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Payments v2 — Refunds & Void (Admin)</h2>
      {error && <div className="mb-3 text-red-600">{error}</div>}

      {/* Search by phone + optional service filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div>
          <label className="block text-sm">Service Type</label>
          <select className="border rounded px-3 py-2 w-full" value={serviceType} onChange={e=>{ setServiceType(e.target.value); setServiceId(''); }}>
            {['Tickets','TourPackage','Taxi','Auto'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {serviceType === 'Tickets' ? (
          <div>
            <label className="block text-sm">Theatre (optional)</label>
            <select className="border rounded px-3 py-2 w-full" value={serviceId} onChange={e=>setServiceId(e.target.value)}>
              <option value="">All</option>
              {theatres.map(th => (
                <option key={th._id} value={th._id}>{th.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-sm">Service ID (optional)</label>
            <input className="border rounded px-3 py-2 w-full" value={serviceId} onChange={e=>setServiceId(e.target.value)} />
          </div>
        )}
        <div className="md:col-span-2">
          <label className="block text-sm">Phone</label>
          <input className="border rounded px-3 py-2 w-full" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="10-digit or full intl" />
        </div>
        <div className="md:col-span-4">
          <button className="px-4 py-2 bg-gray-800 text-white rounded" onClick={doSearch} disabled={searching || !phone}>Search</button>
        </div>
      </div>

      {items.length>0 && (
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-2">Search Results</h3>
          <table className="min-w-full border text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-1 border">Created</th>
                <th className="px-2 py-1 border">MerchantOrder</th>
                <th className="px-2 py-1 border">Booking</th>
                <th className="px-2 py-1 border">Gateway</th>
                <th className="px-2 py-1 border">Amount</th>
                <th className="px-2 py-1 border">Status</th>
                <th className="px-2 py-1 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1 border">{new Date(it.createdAt).toLocaleString()}</td>
                  <td className="px-2 py-1 border">{it.merchantOrderId || '-'}</td>
                  <td className="px-2 py-1 border">{it.bookingId}</td>
                  <td className="px-2 py-1 border">{it.gateway || '-'}</td>
                  <td className="px-2 py-1 border">{it.amount} {it.currency || ''}</td>
                  <td className="px-2 py-1 border">{it.sessionStatus || it.paymentStatus || '-'}</td>
                  <td className="px-2 py-1 border">
                    <div className="flex gap-2">
                      <button className="px-2 py-1 bg-black text-white rounded" onClick={()=>{ const id = it.merchantOrderId||''; setMerchantOrderId(id); setAmount(String(it.amount||'')); setShowManual(true); prefillFromSession(id); }}>Refund</button>
                      <button className="px-2 py-1 bg-gray-700 text-white rounded" onClick={()=>{ setMerchantOrderId(it.merchantOrderId||''); setShowManual(true); }}>Void</button>
                      <button className="px-2 py-1 bg-gray-200 rounded" onClick={()=>{ setMerchantOrderId(it.merchantOrderId||''); checkStatus(); }}>Status</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Manual action form (toggle) */}
      <div className="mb-2">
        <button className="px-3 py-1 bg-gray-100 rounded" onClick={()=>setShowManual(s=>!s)}>
          {showManual ? 'Hide manual form' : 'Show manual form'}
        </button>
      </div>
      {showManual && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm">merchantOrderId</label>
            <input className="border rounded px-3 py-2 w-full" value={merchantOrderId} onChange={e=>setMerchantOrderId(e.target.value)} placeholder="e.g., TIM123_<bookingId>" />
          </div>
          <div>
            <label className="block text-sm">Amount (optional)</label>
            <input type="number" className="border rounded px-3 py-2 w-full" value={amount} onChange={e=>setAmount(e.target.value)} placeholder="leave blank to use session amount" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm">Reason (optional)</label>
            <input className="border rounded px-3 py-2 w-full" value={reason} onChange={e=>setReason(e.target.value)} placeholder="Customer request / Show canceled" />
          </div>
          <div>
            <label className="block text-sm">Refund ID (for reverse)</label>
            <input className="border rounded px-3 py-2 w-full" value={refundId} onChange={e=>setRefundId(e.target.value)} placeholder="Use refund id from provider response" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm">OTP (if enabled by env)</label>
            <input className="border rounded px-3 py-2 w-full" value={otp} onChange={e=>setOtp(e.target.value)} placeholder="Enter OTP or leave blank if disabled" />
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-6">
        <button disabled={loading} className="px-4 py-2 bg-black text-white rounded" onClick={submitRefund}>Initiate Refund</button>
        <button disabled={loading} className="px-4 py-2 bg-gray-800 text-white rounded" onClick={submitVoid}>Void Payment</button>
        <button disabled={loading} className="px-4 py-2 bg-gray-200 rounded" onClick={checkStatus}>Check Refund Status</button>
        <button disabled={loading} className="px-4 py-2 bg-red-600 text-white rounded" onClick={reverseRefund}>Reverse Refund</button>
      </div>

      {loading && <div>Working...</div>}

      {result && (
        <div className="md:col-span-2">
          <label className="block text-sm">Result</label>
          <pre className="border rounded p-3 overflow-auto bg-gray-50 text-sm">{JSON.stringify(result, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}
